import { el } from "../lib/dom";
import { INITIAL_GUEST_LIST } from "../content/initialGuestList";
import type { Guest, GuestListEntry } from "@trou/shared";

const TOKEN_KEY = "trou.admin.token";
const TAB_KEY = "trou.admin.tab";
const BASE = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/$/, "");

type Filter = "all" | "attending" | "declined" | "pending";
type Tab = "rsvps" | "guest-list";
type ListFilter = "all" | "pending" | "responded" | "unmatched";

interface State {
  rsvps: Guest[];
  list: GuestListEntry[];
  unmatched: Guest[];
  // Per-tab state
  rsvpFilter: Filter;
  rsvpSearch: string;
  rsvpEditing: string | null;
  listFilter: ListFilter;
  listSearch: string;
  listEditing: string | null;
  tab: Tab;
}

export function renderAdmin(root: HTMLElement) {
  const page = el("section", { class: "page" });
  page.appendChild(el("p", { class: "page__eyebrow" }, "Admin"));
  page.appendChild(el("h1", { class: "page__title" }, "RSVPs"));

  const slot = el("div", { class: "admin", style: "max-width: 1100px; margin-top: 2rem;" });
  page.appendChild(slot);
  root.appendChild(page);

  const token = sessionStorage.getItem(TOKEN_KEY);
  if (token) {
    void renderDashboard(slot, token);
  } else {
    renderLogin(slot);
  }
}

/* -------------------------------------------------------------------------- */
/*                                   Login                                    */
/* -------------------------------------------------------------------------- */

function renderLogin(slot: HTMLElement) {
  slot.innerHTML = "";
  const form = el("form", { class: "rsvp", style: "max-width: 360px;" });
  const field = el("div", { class: "rsvp__field" });
  field.appendChild(el("label", { class: "rsvp__label", for: "admin-pw" }, "Wagwoord"));
  const input = el("input", {
    id: "admin-pw",
    type: "password",
    class: "rsvp__input",
    autocomplete: "current-password",
  }) as HTMLInputElement;
  field.appendChild(input);
  form.appendChild(field);

  const error = el("p", { style: "color: var(--color-accent); margin-top: 1rem; display: none;" });
  form.appendChild(error);

  const submit = el("button", { class: "rsvp__submit", type: "submit" }, "Teken aan") as HTMLButtonElement;
  form.appendChild(submit);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    error.style.display = "none";
    try {
      const res = await fetch(`${BASE}/admin/login`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ password: input.value }),
      });
      const json = (await res.json()) as { token?: string; message?: string };
      if (!res.ok || !json.token) throw new Error(json.message ?? "Verkeerde wagwoord.");
      sessionStorage.setItem(TOKEN_KEY, json.token);
      await renderDashboard(slot, json.token);
    } catch (err) {
      error.textContent = err instanceof Error ? err.message : "Iets het verkeerd geloop.";
      error.style.display = "block";
      submit.disabled = false;
    }
  });

  slot.appendChild(form);
  setTimeout(() => input.focus({ preventScroll: true }), 100);
}

/* -------------------------------------------------------------------------- */
/*                              Dashboard wiring                              */
/* -------------------------------------------------------------------------- */

async function renderDashboard(slot: HTMLElement, token: string) {
  const state: State = {
    rsvps: [],
    list: [],
    unmatched: [],
    rsvpFilter: "all",
    rsvpSearch: "",
    rsvpEditing: null,
    listFilter: "all",
    listSearch: "",
    listEditing: null,
    tab: (sessionStorage.getItem(TAB_KEY) as Tab) || "rsvps",
  };

  slot.innerHTML = "";
  slot.appendChild(el("p", {}, "Laai..."));

  const refresh = async () => {
    try {
      const [rsvpsRes, listRes, unmatchedRes] = await Promise.all([
        fetch(`${BASE}/admin/rsvps`, { headers: { authorization: `Bearer ${token}` } }),
        fetch(`${BASE}/admin/guest-list`, { headers: { authorization: `Bearer ${token}` } }),
        fetch(`${BASE}/admin/guest-list/unmatched-rsvps`, { headers: { authorization: `Bearer ${token}` } }),
      ]);
      // 401 on any endpoint = session expired; bounce to login
      if (
        rsvpsRes.status === 401 ||
        listRes.status === 401 ||
        unmatchedRes.status === 401
      ) {
        sessionStorage.removeItem(TOKEN_KEY);
        renderLogin(slot);
        return;
      }
      state.rsvps = await safeJsonArray<Guest>(rsvpsRes, "rsvps");
      state.list = await safeJsonArray<GuestListEntry>(listRes, "guest-list");
      state.unmatched = await safeJsonArray<Guest>(unmatchedRes, "unmatched-rsvps");
      paint(slot, state, token, refresh);
    } catch (err) {
      slot.innerHTML = "";
      slot.appendChild(
        el("p", { style: "color: var(--color-accent);" }, err instanceof Error ? err.message : "Fout"),
      );
    }
  };

  await refresh();
}

/**
 * Parse a JSON response into an array of T. Returns [] if the response was
 * not OK or the body wasn't an array — protects the UI from transient API
 * errors during redeploys (when an endpoint may briefly 500/404).
 */
async function safeJsonArray<T>(res: Response, label: string): Promise<T[]> {
  if (!res.ok) {
    console.warn(`[admin] ${label} returned ${res.status}`);
    return [];
  }
  const data = await res.json().catch(() => null);
  if (!Array.isArray(data)) {
    console.warn(`[admin] ${label} returned non-array:`, data);
    return [];
  }
  return data as T[];
}

/* -------------------------------------------------------------------------- */
/*                                 Painting                                   */
/* -------------------------------------------------------------------------- */

function paint(slot: HTMLElement, state: State, token: string, refresh: () => Promise<void>) {
  slot.innerHTML = "";

  /* --------------------------- top-level tab strip ------------------------ */
  const tabs = el("div", { class: "admin__tabs" });
  for (const [val, label] of [
    ["rsvps", "RSVPs"],
    ["guest-list", "Gas-lys"],
  ] as const) {
    const t = el(
      "button",
      {
        class: `admin__tab${state.tab === val ? " is-active" : ""}`,
        type: "button",
      },
      label,
    );
    t.addEventListener("click", () => {
      state.tab = val;
      sessionStorage.setItem(TAB_KEY, val);
      paint(slot, state, token, refresh);
    });
    tabs.appendChild(t);
  }
  slot.appendChild(tabs);

  if (state.tab === "rsvps") {
    paintRsvpsTab(slot, state, token, refresh);
  } else {
    paintGuestListTab(slot, state, token, refresh);
  }
}

/* -------------------------------------------------------------------------- */
/*                               RSVPs tab                                    */
/* -------------------------------------------------------------------------- */

function paintRsvpsTab(slot: HTMLElement, state: State, token: string, refresh: () => Promise<void>) {
  const totals = state.rsvps.reduce(
    (acc, g) => {
      acc.total++;
      if (g.status === "attending") acc.attending++;
      else if (g.status === "declined") acc.declined++;
      else acc.pending++;
      return acc;
    },
    { total: 0, attending: 0, declined: 0, pending: 0 },
  );

  // Summary tiles
  const summary = el("div", { class: "admin__summary" });
  for (const [label, n] of [
    ["Kom", totals.attending],
    ["Kan nie", totals.declined],
    ["Wag", totals.pending],
    ["Totaal", totals.total],
  ] as const) {
    summary.appendChild(
      el(
        "div",
        { class: "admin__tile" },
        el("p", { class: "admin__tile-label" }, String(label)),
        el("p", { class: "admin__tile-value" }, String(n)),
      ),
    );
  }
  slot.appendChild(summary);

  // Action row
  const actions = el("div", { class: "admin__actions" });
  const csvBtn = el("a", { href: `${BASE}/admin/rsvps.csv`, class: "admin__btn" }, "Laai CSV af");
  csvBtn.addEventListener("click", (e) => {
    e.preventDefault();
    void downloadCsv(token);
  });
  const refreshBtn = el("button", { class: "admin__btn", type: "button" }, "Verfris");
  refreshBtn.addEventListener("click", () => void refresh());
  const logoutBtn = el("button", { class: "admin__btn admin__btn--ghost", type: "button" }, "Teken uit");
  logoutBtn.addEventListener("click", () => {
    sessionStorage.removeItem(TOKEN_KEY);
    renderLogin(slot);
  });
  actions.append(csvBtn, refreshBtn, logoutBtn);
  slot.appendChild(actions);

  // Song wall
  const songs = state.rsvps
    .filter((g) => g.songRequest && g.songRequest.trim() && g.status === "attending")
    .map((g) => ({ id: g.id, song: g.songRequest!.trim(), name: g.fullName }));
  if (songs.length > 0) {
    const wall = el("section", { class: "admin__panel" });
    const head = el("div", { class: "admin__panel-head" });
    head.appendChild(
      el("div", {},
        el("p", { class: "admin__panel-eyebrow" }, "Vir die DJ"),
        el("h2", { class: "admin__panel-title" }, `${songs.length} versoekliedjies`),
      ),
    );
    const copyBtn = el("button", { class: "admin__btn admin__btn--small", type: "button" }, "Kopieer lys");
    copyBtn.addEventListener("click", () => {
      const text = songs.map((s) => `${s.song}  —  (${s.name})`).join("\n");
      void navigator.clipboard.writeText(text).then(() => {
        copyBtn.textContent = "Gekopieer ✓";
        setTimeout(() => (copyBtn.textContent = "Kopieer lys"), 1800);
      });
    });
    head.appendChild(copyBtn);
    wall.appendChild(head);

    const list = el("ul", { class: "admin__songs" });
    for (const { id, song, name } of songs) {
      const item = el(
        "li",
        { class: "admin__song" },
        el("div", { class: "admin__song-text" },
          el("span", { class: "admin__song-track" }, song),
          el("span", { class: "admin__song-name" }, name),
        ),
      );
      const removeBtn = el(
        "button",
        { class: "admin__icon-btn admin__icon-btn--danger admin__icon-btn--small", type: "button", title: `Verwyder "${song}" van die lys` },
        "✕",
      );
      removeBtn.addEventListener("click", async () => {
        if (!confirm(`Verwyder "${song}" van die liedjie-lys?`)) return;
        removeBtn.disabled = true;
        try {
          const res = await fetch(`${BASE}/admin/rsvps/${id}`, {
            method: "PATCH",
            headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
            body: JSON.stringify({ songRequest: null }),
          });
          if (!res.ok) throw new Error("Kon nie verwyder nie.");
          await refresh();
        } catch (err) {
          alert(err instanceof Error ? err.message : "Fout");
          removeBtn.disabled = false;
        }
      });
      item.appendChild(removeBtn);
      list.appendChild(item);
    }
    wall.appendChild(list);
    slot.appendChild(wall);
  }

  // Message wall
  const messages = state.rsvps
    .filter((g) => g.message && g.message.trim())
    .map((g) => ({ message: g.message!.trim(), name: g.fullName }));
  if (messages.length > 0) {
    const wall = el("section", { class: "admin__panel" });
    wall.appendChild(
      el("div", { class: "admin__panel-head" },
        el("div", {},
          el("p", { class: "admin__panel-eyebrow" }, "Boodskappe"),
          el("h2", { class: "admin__panel-title" }, `${messages.length} boodskappe`),
        ),
      ),
    );
    const grid = el("div", { class: "admin__messages" });
    for (const m of messages) {
      grid.appendChild(
        el("blockquote", { class: "admin__message" },
          el("p", {}, `"${m.message}"`),
          el("footer", {}, `— ${m.name}`),
        ),
      );
    }
    wall.appendChild(grid);
    slot.appendChild(wall);
  }

  // Search + filter + table
  const tableWrap = el("section", { class: "admin__panel" });
  const tableHead = el("div", { class: "admin__panel-head" });
  const titleEl = el("h2", { class: "admin__panel-title" }, "Volledige lys");
  tableHead.appendChild(
    el("div", {},
      el("p", { class: "admin__panel-eyebrow" }, "Alle RSVPs"),
      titleEl,
    ),
  );
  tableWrap.appendChild(tableHead);

  const controls = el("div", { class: "admin__controls" });
  const searchInput = el("input", {
    type: "search", class: "admin__search",
    placeholder: "Soek naam of e-pos...", value: state.rsvpSearch,
  }) as HTMLInputElement;
  searchInput.addEventListener("input", () => {
    state.rsvpSearch = searchInput.value;
    repaintTable();
  });
  controls.appendChild(searchInput);

  const statusCounts = {
    all: state.rsvps.length,
    attending: totals.attending,
    declined: totals.declined,
    pending: totals.pending,
  } as const;

  const filterChips = el("div", { class: "admin__filters" });
  const chipRefs = new Map<Filter, HTMLElement>();
  for (const [val, label] of [
    ["all", "Almal"],
    ["attending", "Kom"],
    ["declined", "Kan nie"],
    ["pending", "Wag"],
  ] as const) {
    const chip = el("button", {
      class: `admin__chip${state.rsvpFilter === val ? " is-active" : ""}`,
      type: "button", "data-filter": val,
    });
    chip.appendChild(el("span", { class: "admin__chip-label" }, label));
    chip.appendChild(el("span", { class: "admin__chip-count" }, String(statusCounts[val])));
    chip.addEventListener("click", () => {
      state.rsvpFilter = val;
      for (const [v, c] of chipRefs) c.classList.toggle("is-active", v === val);
      repaintTable();
    });
    filterChips.appendChild(chip);
    chipRefs.set(val, chip);
  }
  controls.appendChild(filterChips);
  tableWrap.appendChild(controls);

  const tableContainer = el("div", { class: "admin__table-wrap" });
  tableWrap.appendChild(tableContainer);
  slot.appendChild(tableWrap);

  function repaintTable() {
    const filtered = state.rsvps.filter((g) => {
      if (state.rsvpFilter !== "all" && g.status !== state.rsvpFilter) return false;
      if (state.rsvpSearch) {
        const q = state.rsvpSearch.toLowerCase();
        if (!g.fullName.toLowerCase().includes(q) && !(g.email ?? "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
    titleEl.textContent = formatResultsTitle(filtered.length, state);
    tableContainer.innerHTML = "";
    if (filtered.length === 0) {
      tableContainer.appendChild(
        el("p", { style: "color: var(--color-ink-soft); padding: 1rem 0;" }, "Geen RSVPs pas hierdie filter nie."),
      );
      return;
    }
    tableContainer.appendChild(buildRsvpTable(filtered, state, token, refresh));
  }
  repaintTable();
}

function formatResultsTitle(count: number, state: State): string {
  const noun = (n: number, sing: string, plur: string) => (n === 1 ? sing : plur);
  if (state.rsvpSearch) return `${count} ${noun(count, "resultaat", "resultate")} vir "${state.rsvpSearch}"`;
  switch (state.rsvpFilter) {
    case "attending": return `${count} kom`;
    case "declined":  return `${count} kan nie kom nie`;
    case "pending":   return `${count} wag nog`;
    default:          return `${count} RSVP${count === 1 ? "" : "s"}`;
  }
}

function buildRsvpTable(rows: Guest[], state: State, token: string, refresh: () => Promise<void>): HTMLElement {
  const table = el("table", { class: "admin__table" });
  const thead = el("thead", {},
    el("tr", {},
      ...["Naam", "E-pos", "Status", "Liedjie", "Boodskap", "Wanneer", ""].map((h) => el("th", {}, h)),
    ),
  );
  table.appendChild(thead);
  const tbody = el("tbody", {});
  for (const g of rows) {
    if (state.rsvpEditing === g.id) tbody.appendChild(buildRsvpEditRow(g, state, token, refresh));
    else tbody.appendChild(buildRsvpViewRow(g, state, token, refresh));
  }
  table.appendChild(tbody);
  return table;
}

function buildRsvpViewRow(g: Guest, state: State, token: string, refresh: () => Promise<void>): HTMLElement {
  const row = el("tr", { class: "admin__row" });
  row.appendChild(el("td", { class: "admin__cell admin__cell--name" }, g.fullName));
  row.appendChild(el("td", { class: "admin__cell" }, g.email ?? "—"));
  row.appendChild(
    el("td", { class: "admin__cell" },
      el("span", { class: `admin__status admin__status--${g.status}` },
        g.status === "attending" ? "Kom" : g.status === "declined" ? "Kan nie" : "Wag",
      ),
    ),
  );
  row.appendChild(el("td", { class: "admin__cell" }, g.songRequest ?? "—"));
  row.appendChild(el("td", { class: "admin__cell" }, g.message ?? "—"));
  row.appendChild(el("td", { class: "admin__cell admin__cell--time" }, formatDate(g.updatedAt)));
  const actions = el("td", { class: "admin__cell admin__cell--actions" });
  const editBtn = el("button", { class: "admin__icon-btn", type: "button", title: "Wysig" }, "✎");
  editBtn.addEventListener("click", () => {
    state.rsvpEditing = g.id;
    void refresh();
  });
  const delBtn = el("button", { class: "admin__icon-btn admin__icon-btn--danger", type: "button", title: "Verwyder" }, "✕");
  delBtn.addEventListener("click", async () => {
    if (!confirm(`Verwyder die RSVP van ${g.fullName}?`)) return;
    try {
      const res = await fetch(`${BASE}/admin/rsvps/${g.id}`, {
        method: "DELETE", headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Kon nie verwyder nie.");
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Fout");
    }
  });
  actions.append(editBtn, delBtn);
  row.appendChild(actions);
  return row;
}

function buildRsvpEditRow(g: Guest, state: State, token: string, refresh: () => Promise<void>): HTMLElement {
  const row = el("tr", { class: "admin__row admin__row--edit" });
  const nameInput = el("input", { class: "admin__edit-input", value: g.fullName }) as HTMLInputElement;
  const emailInput = el("input", { class: "admin__edit-input", type: "email", value: g.email ?? "" }) as HTMLInputElement;
  const statusSelect = el("select", { class: "admin__edit-input" }) as HTMLSelectElement;
  for (const [v, label] of [["attending", "Kom"], ["declined", "Kan nie"], ["pending", "Wag"]] as const) {
    const opt = el("option", { value: v }, label) as HTMLOptionElement;
    if (g.status === v) opt.selected = true;
    statusSelect.appendChild(opt);
  }
  const songInput = el("input", { class: "admin__edit-input", value: g.songRequest ?? "" }) as HTMLInputElement;
  const msgInput = el("input", { class: "admin__edit-input", value: g.message ?? "" }) as HTMLInputElement;

  row.appendChild(el("td", { class: "admin__cell" }, nameInput));
  row.appendChild(el("td", { class: "admin__cell" }, emailInput));
  row.appendChild(el("td", { class: "admin__cell" }, statusSelect));
  row.appendChild(el("td", { class: "admin__cell" }, songInput));
  row.appendChild(el("td", { class: "admin__cell" }, msgInput));
  row.appendChild(el("td", { class: "admin__cell admin__cell--time" }, formatDate(g.updatedAt)));

  const actions = el("td", { class: "admin__cell admin__cell--actions" });
  const saveBtn = el("button", { class: "admin__icon-btn", type: "button", title: "Stoor" }, "✓");
  saveBtn.addEventListener("click", async () => {
    const body: Record<string, string | null> = {};
    if (nameInput.value !== g.fullName) body.fullName = nameInput.value.trim();
    if (emailInput.value !== (g.email ?? "")) body.email = emailInput.value.trim() || null;
    if (statusSelect.value !== g.status) body.status = statusSelect.value;
    if (songInput.value !== (g.songRequest ?? "")) body.songRequest = songInput.value.trim() || null;
    if (msgInput.value !== (g.message ?? "")) body.message = msgInput.value.trim() || null;
    if (Object.keys(body).length === 0) {
      state.rsvpEditing = null;
      await refresh();
      return;
    }
    try {
      const res = await fetch(`${BASE}/admin/rsvps/${g.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message ?? "Kon nie stoor nie.");
      }
      state.rsvpEditing = null;
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Fout");
    }
  });
  const cancelBtn = el("button", { class: "admin__icon-btn admin__icon-btn--ghost", type: "button", title: "Kanselleer" }, "↺");
  cancelBtn.addEventListener("click", () => {
    state.rsvpEditing = null;
    void refresh();
  });
  actions.append(saveBtn, cancelBtn);
  row.appendChild(actions);
  return row;
}

/* -------------------------------------------------------------------------- */
/*                               Gas-lys tab                                  */
/* -------------------------------------------------------------------------- */

function paintGuestListTab(slot: HTMLElement, state: State, token: string, refresh: () => Promise<void>) {
  const totalInvited = state.list.length;
  const responded = state.list.filter((e) => e.matched !== null).length;
  const pending = totalInvited - responded;
  const unknown = state.unmatched.length;
  const responseRate = totalInvited > 0 ? Math.round((responded / totalInvited) * 100) : 0;

  // Summary tiles
  const summary = el("div", { class: "admin__summary" });
  for (const [label, n] of [
    ["Genooi", totalInvited],
    ["Geantwoord", `${responded}${totalInvited > 0 ? ` (${responseRate}%)` : ""}`],
    ["Wag", pending],
    ["Onbekend", unknown],
  ] as const) {
    summary.appendChild(
      el("div", { class: "admin__tile" },
        el("p", { class: "admin__tile-label" }, String(label)),
        el("p", { class: "admin__tile-value" }, String(n)),
      ),
    );
  }
  slot.appendChild(summary);

  // Add panel — single name + bulk paste + initial-list import
  slot.appendChild(buildAddPanel(state, token, refresh));

  // List view with filters
  const tableWrap = el("section", { class: "admin__panel" });
  const tableHead = el("div", { class: "admin__panel-head" });
  const titleEl = el("h2", { class: "admin__panel-title" }, "Lys");
  tableHead.appendChild(
    el("div", {},
      el("p", { class: "admin__panel-eyebrow" }, "Genooide gaste"),
      titleEl,
    ),
  );
  tableWrap.appendChild(tableHead);

  const controls = el("div", { class: "admin__controls" });
  const searchInput = el("input", {
    type: "search", class: "admin__search",
    placeholder: "Soek naam...", value: state.listSearch,
  }) as HTMLInputElement;
  searchInput.addEventListener("input", () => {
    state.listSearch = searchInput.value;
    repaintTable();
  });
  controls.appendChild(searchInput);

  const counts = {
    all: totalInvited,
    pending: pending,
    responded: responded,
    unmatched: unknown,
  } as const;

  const filterChips = el("div", { class: "admin__filters" });
  const chipRefs = new Map<ListFilter, HTMLElement>();
  for (const [val, label] of [
    ["all", "Almal"],
    ["pending", "Wag"],
    ["responded", "Geantwoord"],
    ["unmatched", "Onbekend"],
  ] as const) {
    const chip = el("button", {
      class: `admin__chip${state.listFilter === val ? " is-active" : ""}`,
      type: "button", "data-filter": val,
    });
    chip.appendChild(el("span", { class: "admin__chip-label" }, label));
    chip.appendChild(el("span", { class: "admin__chip-count" }, String(counts[val])));
    chip.addEventListener("click", () => {
      state.listFilter = val;
      for (const [v, c] of chipRefs) c.classList.toggle("is-active", v === val);
      repaintTable();
    });
    filterChips.appendChild(chip);
    chipRefs.set(val, chip);
  }
  controls.appendChild(filterChips);
  tableWrap.appendChild(controls);

  const tableContainer = el("div", { class: "admin__table-wrap" });
  tableWrap.appendChild(tableContainer);
  slot.appendChild(tableWrap);

  function repaintTable() {
    if (state.listFilter === "unmatched") {
      // Different table: orphaned RSVPs that didn't match any list entry
      const filtered = state.unmatched.filter((g) => {
        if (!state.listSearch) return true;
        const q = state.listSearch.toLowerCase();
        return g.fullName.toLowerCase().includes(q) || (g.email ?? "").toLowerCase().includes(q);
      });
      titleEl.textContent =
        state.listSearch
          ? `${filtered.length} resultate vir "${state.listSearch}" (Onbekend)`
          : `${filtered.length} onbekende RSVP${filtered.length === 1 ? "" : "s"}`;
      tableContainer.innerHTML = "";
      if (filtered.length === 0) {
        tableContainer.appendChild(
          el("p", { style: "color: var(--color-ink-soft); padding: 1rem 0;" },
            "Geen onbekende RSVPs nie. Almal pas op die lys."),
        );
        return;
      }
      tableContainer.appendChild(buildUnmatchedTable(filtered, state, token, refresh));
    } else {
      const filtered = state.list.filter((e) => {
        if (state.listFilter === "pending" && e.matched !== null) return false;
        if (state.listFilter === "responded" && e.matched === null) return false;
        if (state.listSearch) {
          const q = state.listSearch.toLowerCase();
          if (!e.fullName.toLowerCase().includes(q)) return false;
        }
        return true;
      });
      titleEl.textContent = formatListTitle(filtered.length, state);
      tableContainer.innerHTML = "";
      if (filtered.length === 0) {
        tableContainer.appendChild(
          el("p", { style: "color: var(--color-ink-soft); padding: 1rem 0;" },
            totalInvited === 0
              ? "Die lys is leeg. Importeer of voeg name by."
              : "Geen lys-inskrywings pas hierdie filter nie."),
        );
        return;
      }
      tableContainer.appendChild(buildGuestListTable(filtered, state, token, refresh));
    }
  }
  repaintTable();
}

function formatListTitle(count: number, state: State): string {
  if (state.listSearch) return `${count} resultate vir "${state.listSearch}"`;
  switch (state.listFilter) {
    case "pending":   return `${count} wag op antwoord`;
    case "responded": return `${count} geantwoord`;
    default:          return `${count} ${count === 1 ? "naam" : "name"}`;
  }
}

function buildAddPanel(state: State, token: string, refresh: () => Promise<void>): HTMLElement {
  const panel = el("section", { class: "admin__panel" });
  panel.appendChild(
    el("div", { class: "admin__panel-head" },
      el("div", {},
        el("p", { class: "admin__panel-eyebrow" }, "Voeg name by die lys"),
        el("h2", { class: "admin__panel-title" }, "Nuwe inskrywings"),
      ),
    ),
  );

  // Inline single-add row
  const singleRow = el("form", { class: "admin__add-row" });
  const singleInput = el("input", {
    type: "text", class: "admin__add-input", placeholder: "Voornaam en van...",
  }) as HTMLInputElement;
  singleRow.appendChild(singleInput);
  const singleBtn = el("button", { class: "admin__btn admin__btn--small", type: "submit" }, "Voeg by");
  singleRow.appendChild(singleBtn);
  singleRow.addEventListener("submit", async (e) => {
    e.preventDefault();
    const v = singleInput.value.trim();
    if (!v) return;
    try {
      const res = await fetch(`${BASE}/admin/guest-list`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ fullName: v }),
      });
      if (!res.ok) throw new Error("Kon nie byvoeg nie.");
      singleInput.value = "";
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Fout");
    }
  });
  panel.appendChild(singleRow);

  // Bulk-paste expander
  const bulkWrap = el("details", { class: "admin__bulk" });
  bulkWrap.appendChild(el("summary", {}, "Voeg veelvuldige name by (een per lyn)"));
  const bulkArea = el("textarea", {
    class: "admin__bulk-area", rows: "6",
    placeholder: "Pieter du Toit\nAnneke du Toit\nKobus van der Merwe",
  }) as HTMLTextAreaElement;
  bulkWrap.appendChild(bulkArea);
  const bulkBtn = el("button", { class: "admin__btn admin__btn--small", type: "button" }, "Voeg almal by") as HTMLButtonElement;
  bulkBtn.addEventListener("click", async () => {
    const names = bulkArea.value.split(/\r?\n/).map((n) => n.trim()).filter((n) => n.length > 0);
    if (names.length === 0) return;
    bulkBtn.disabled = true;
    try {
      const res = await fetch(`${BASE}/admin/guest-list/bulk`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ names }),
      });
      if (!res.ok) throw new Error("Kon nie byvoeg nie.");
      const body = (await res.json()) as { added: number; skipped: number };
      bulkArea.value = "";
      bulkWrap.removeAttribute("open");
      alert(`${body.added} naam${body.added === 1 ? "" : "e"} bygevoeg, ${body.skipped} oorgeslaan (alreeds op lys).`);
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Fout");
    } finally {
      bulkBtn.disabled = false;
    }
  });
  bulkWrap.appendChild(bulkBtn);
  panel.appendChild(bulkWrap);

  // Initial-list import (only useful while the list is empty)
  if (state.list.length === 0 && INITIAL_GUEST_LIST.length > 0) {
    const seedBtn = el("button", { class: "admin__btn", type: "button", style: "margin-top: 1rem;" },
      `Importeer Hannes & Bianca se aanvanklike lys (${INITIAL_GUEST_LIST.length} name)`) as HTMLButtonElement;
    seedBtn.addEventListener("click", async () => {
      if (!confirm(`Voeg ${INITIAL_GUEST_LIST.length} name uit die aanvanklike lys by?`)) return;
      seedBtn.disabled = true;
      try {
        const res = await fetch(`${BASE}/admin/guest-list/bulk`, {
          method: "POST",
          headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
          body: JSON.stringify({ names: INITIAL_GUEST_LIST }),
        });
        if (!res.ok) throw new Error("Kon nie importeer nie.");
        const body = (await res.json()) as { added: number; skipped: number };
        alert(`${body.added} bygevoeg, ${body.skipped} oorgeslaan.`);
        await refresh();
      } catch (err) {
        alert(err instanceof Error ? err.message : "Fout");
      } finally {
        seedBtn.disabled = false;
      }
    });
    panel.appendChild(seedBtn);
  }

  return panel;
}

function buildGuestListTable(rows: GuestListEntry[], state: State, token: string, refresh: () => Promise<void>): HTMLElement {
  const table = el("table", { class: "admin__table" });
  table.appendChild(
    el("thead", {},
      el("tr", {},
        ...["Naam", "Status", "Reaksie", ""].map((h) => el("th", {}, h)),
      ),
    ),
  );
  const tbody = el("tbody", {});
  for (const e of rows) {
    if (state.listEditing === e.id) tbody.appendChild(buildListEditRow(e, state, token, refresh));
    else tbody.appendChild(buildListViewRow(e, state, token, refresh));
  }
  table.appendChild(tbody);
  return table;
}

function buildListViewRow(e: GuestListEntry, state: State, token: string, refresh: () => Promise<void>): HTMLElement {
  const row = el("tr", { class: "admin__row" });
  row.appendChild(el("td", { class: "admin__cell admin__cell--name" }, e.fullName));

  // Status from matched RSVP
  const matched = e.matched;
  let statusLabel = "Wag";
  let statusClass = "pending";
  if (matched) {
    if (matched.status === "attending")  { statusLabel = "Kom";     statusClass = "attending"; }
    else if (matched.status === "declined") { statusLabel = "Kan nie";  statusClass = "declined"; }
    else { statusLabel = "Onbekend"; statusClass = "pending"; }
  }
  row.appendChild(
    el("td", { class: "admin__cell" },
      el("span", { class: `admin__status admin__status--${statusClass}` }, statusLabel),
    ),
  );

  // Reaksie cell — RSVP info or a "Koppel" action
  const reactCell = el("td", { class: "admin__cell" });
  if (matched) {
    reactCell.appendChild(
      el("div", { style: "font-size: var(--type-sm);" },
        el("span", {}, matched.fullName),
        el("span", { style: "color: var(--color-ink-faint); margin-left: 0.5rem;" }, matched.email ?? ""),
      ),
    );
    const unlinkBtn = el("button", {
      class: "admin__btn admin__btn--small admin__btn--ghost",
      type: "button", style: "margin-top: 0.4rem;",
    }, "Ontkoppel");
    unlinkBtn.addEventListener("click", async () => {
      try {
        const res = await fetch(`${BASE}/admin/guest-list/${e.id}/link`, {
          method: "DELETE", headers: { authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Kon nie ontkoppel nie.");
        await refresh();
      } catch (err) {
        alert(err instanceof Error ? err.message : "Fout");
      }
    });
    reactCell.appendChild(unlinkBtn);
  } else if (state.unmatched.length > 0) {
    const linkSelect = el("select", { class: "admin__edit-input" }) as HTMLSelectElement;
    linkSelect.appendChild(el("option", { value: "" }, "— Koppel aan onbekende RSVP —"));
    for (const u of state.unmatched) {
      linkSelect.appendChild(el("option", { value: u.id }, u.email ? `${u.fullName} (${u.email})` : u.fullName));
    }
    linkSelect.addEventListener("change", async () => {
      const guestId = linkSelect.value;
      if (!guestId) return;
      try {
        const res = await fetch(`${BASE}/admin/guest-list/${e.id}/link`, {
          method: "POST",
          headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
          body: JSON.stringify({ guestId }),
        });
        if (!res.ok) throw new Error("Kon nie koppel nie.");
        await refresh();
      } catch (err) {
        alert(err instanceof Error ? err.message : "Fout");
      }
    });
    reactCell.appendChild(linkSelect);
  } else {
    reactCell.appendChild(el("span", { style: "color: var(--color-ink-faint);" }, "—"));
  }
  row.appendChild(reactCell);

  // Actions
  const actions = el("td", { class: "admin__cell admin__cell--actions" });
  const editBtn = el("button", { class: "admin__icon-btn", type: "button", title: "Wysig" }, "✎");
  editBtn.addEventListener("click", () => {
    state.listEditing = e.id;
    void refresh();
  });
  const delBtn = el("button", { class: "admin__icon-btn admin__icon-btn--danger", type: "button", title: "Verwyder" }, "✕");
  delBtn.addEventListener("click", async () => {
    if (!confirm(`Verwyder "${e.fullName}" van die lys?`)) return;
    try {
      const res = await fetch(`${BASE}/admin/guest-list/${e.id}`, {
        method: "DELETE", headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Kon nie verwyder nie.");
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Fout");
    }
  });
  actions.append(editBtn, delBtn);
  row.appendChild(actions);
  return row;
}

function buildListEditRow(e: GuestListEntry, state: State, token: string, refresh: () => Promise<void>): HTMLElement {
  const row = el("tr", { class: "admin__row admin__row--edit" });
  const nameInput = el("input", { class: "admin__edit-input", value: e.fullName }) as HTMLInputElement;
  row.appendChild(el("td", { class: "admin__cell" }, nameInput));
  row.appendChild(el("td", { class: "admin__cell", colspan: "2" }, ""));
  const actions = el("td", { class: "admin__cell admin__cell--actions" });
  const saveBtn = el("button", { class: "admin__icon-btn", type: "button", title: "Stoor" }, "✓");
  saveBtn.addEventListener("click", async () => {
    const v = nameInput.value.trim();
    if (!v || v === e.fullName) {
      state.listEditing = null;
      await refresh();
      return;
    }
    try {
      const res = await fetch(`${BASE}/admin/guest-list/${e.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ fullName: v }),
      });
      if (!res.ok) throw new Error("Kon nie stoor nie.");
      state.listEditing = null;
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Fout");
    }
  });
  const cancelBtn = el("button", { class: "admin__icon-btn admin__icon-btn--ghost", type: "button", title: "Kanselleer" }, "↺");
  cancelBtn.addEventListener("click", () => {
    state.listEditing = null;
    void refresh();
  });
  actions.append(saveBtn, cancelBtn);
  row.appendChild(actions);
  return row;
}

function buildUnmatchedTable(rows: Guest[], state: State, token: string, refresh: () => Promise<void>): HTMLElement {
  const table = el("table", { class: "admin__table" });
  table.appendChild(
    el("thead", {},
      el("tr", {},
        ...["Naam", "E-pos", "Status", "Wanneer", "Aksies"].map((h) => el("th", {}, h)),
      ),
    ),
  );
  const tbody = el("tbody", {});
  for (const g of rows) {
    const row = el("tr", { class: "admin__row" });
    row.appendChild(el("td", { class: "admin__cell admin__cell--name" }, g.fullName));
    row.appendChild(el("td", { class: "admin__cell" }, g.email ?? "—"));
    row.appendChild(
      el("td", { class: "admin__cell" },
        el("span", { class: `admin__status admin__status--${g.status}` },
          g.status === "attending" ? "Kom" : g.status === "declined" ? "Kan nie" : "Wag",
        ),
      ),
    );
    row.appendChild(el("td", { class: "admin__cell admin__cell--time" }, formatDate(g.updatedAt)));

    const actionCell = el("td", { class: "admin__cell" });
    const linkSelect = el("select", { class: "admin__edit-input" }) as HTMLSelectElement;
    const pendingEntries = state.list.filter((e) => e.matched === null);
    if (pendingEntries.length === 0) {
      actionCell.appendChild(el("span", { style: "color: var(--color-ink-faint);" }, "Geen onkoppelde lys-inskrywings"));
    } else {
      linkSelect.appendChild(el("option", { value: "" }, "— Koppel aan lys-inskrywing —"));
      for (const e of pendingEntries) {
        linkSelect.appendChild(el("option", { value: e.id }, e.fullName));
      }
      linkSelect.addEventListener("change", async () => {
        const entryId = linkSelect.value;
        if (!entryId) return;
        try {
          const res = await fetch(`${BASE}/admin/guest-list/${entryId}/link`, {
            method: "POST",
            headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
            body: JSON.stringify({ guestId: g.id }),
          });
          if (!res.ok) throw new Error("Kon nie koppel nie.");
          await refresh();
        } catch (err) {
          alert(err instanceof Error ? err.message : "Fout");
        }
      });
      actionCell.appendChild(linkSelect);
    }
    row.appendChild(actionCell);
    tbody.appendChild(row);
  }
  table.appendChild(tbody);
  return table;
}

/* -------------------------------------------------------------------------- */

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("af-ZA", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

async function downloadCsv(token: string) {
  const res = await fetch(`${BASE}/admin/rsvps.csv`, { headers: { authorization: `Bearer ${token}` } });
  if (!res.ok) return;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "rsvps.csv";
  a.click();
  URL.revokeObjectURL(url);
}
